import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * The shared field surface. Height, radius, border, focus ring and disabled
 * treatment are defined ONCE here; select and textarea compose the same string
 * so the three cannot drift apart.
 */
export const fieldBase =
  "w-full rounded border border-border bg-input px-3 text-sm text-foreground " +
  "placeholder:text-muted-foreground transition-colors " +
  "hover:border-secondary/40 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring " +
  "disabled:cursor-not-allowed disabled:bg-muted disabled:text-disabled disabled:hover:border-border " +
  // aria-invalid is how a field says it failed validation; it should LOOK like
  // it failed without the caller remembering to add a class.
  "aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/40";

/**
 * Two field heights, NAMED -- and named the same as the button's, so a toolbar
 * is one decision rather than one per control. A height pasted into a class
 * string can only be got consistent; a size with a name can be got right.
 */
export type FieldSize = "sm" | "default";
export const fieldSize: Record<FieldSize, string> = { sm: "h-8", default: "h-9" };

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  size?: FieldSize;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, size = "default", ...props }, ref) => (
    <input ref={ref} className={cn(fieldBase, fieldSize[size], className)} {...props} />
  ),
);
Input.displayName = "Input";

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  size?: FieldSize;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, size = "default", ...props }, ref) => (
    // `appearance-none` is deliberately NOT set: the platform's own arrow is
    // the only part of a select that reliably says "this opens", and drawing a
    // replacement means drawing it for every platform.
    <select ref={ref} className={cn(fieldBase, fieldSize[size], "pr-8", className)} {...props} />
  ),
);
Select.displayName = "Select";

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, rows = 4, ...props }, ref) => (
    <textarea ref={ref} rows={rows} className={cn(fieldBase, "py-2 leading-relaxed", className)} {...props} />
  ),
);
Textarea.displayName = "Textarea";

/**
 * A labelled field.
 *
 * The label is a real <label> tied to the control by id, because a placeholder
 * is not a label: it disappears the moment somebody types, and this panel has
 * forms with eight fields where "which box was the meta description" is a
 * question that gets asked.
 */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <label htmlFor={htmlFor} className="block text-xs font-medium text-secondary">
        {label}
      </label>
      {children}
      {/* The hint is hidden once there is an error: two lines of small grey
          text under a red border is a guessing game about which one is the
          problem. */}
      {error ? (
        <p className="text-xs text-destructive-ink">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
